// App del repartidor por token público, SIN login (/reparto/:token).
// El repartidor abre su ruta del día en el celular y registra la entrega de cada
// parada: temperatura de llegada, receptor, firma y foto (prueba de entrega).
// Mismo patrón de token que el andón; escribe vía reparto_entregar.
import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Truck, MapPin, CheckCircle2, AlertTriangle, Clock, Phone, ChevronLeft, Thermometer } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { FirmaCanvas } from '@/features/medical/FirmaCanvas';
import { FotoCaptura } from './FotoCaptura';

interface Entrega { estado: string; hora_real: string; receptor: string | null; temp: number | null; incidencia: string | null }
interface Parada {
  contrato_id: string; cliente: string; direccion: string; orden: number;
  hora_objetivo: string | null; telefono: string | null; raciones: number; servicios: string | null;
  entrega: Entrega | null;
}
interface Despacho { id: string; codigo: string; fecha: string; estado: string; placa: string | null; chofer: string | null; paradas: Parada[] }
interface Tablero { valido: boolean; ruta?: string; despacho: Despacho | null }

const INCIDENCIAS = ['Faltante de raciones', 'Cliente ausente', 'Rechazo por calidad', 'Dirección incorrecta', 'Otro'];

export function RepartoApp() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const [abierta, setAbierta] = useState<string | null>(null);

  const { data, isLoading } = useQuery<Tablero>({
    queryKey: ['reparto', token],
    queryFn: async () => (await apiClient.post<Tablero>('/rpc/reparto_tablero', { _token: token })).data,
    refetchInterval: 20000, staleTime: 0, retry: 1,
  });

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-900 text-white"><Loader2 className="mr-2 h-7 w-7 animate-spin" /> Cargando ruta…</div>;
  }
  if (!data?.valido) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-slate-900 p-6 text-center text-white">
        <AlertTriangle className="h-10 w-10 text-amber-400" />
        <p className="text-xl font-bold">Enlace de reparto no válido</p>
        <p className="text-white/60">Pide a la oficina el enlace correcto de tu ruta.</p>
      </div>
    );
  }

  const d = data.despacho;
  const parada = d?.paradas.find((p) => p.contrato_id === abierta) ?? null;

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900">
      <header className="sticky top-0 z-10 bg-emerald-700 px-4 py-3 text-white shadow">
        <div className="flex items-center gap-2">
          {parada ? (
            <button onClick={() => setAbierta(null)} className="rounded-full p-1 active:bg-white/20"><ChevronLeft className="h-5 w-5" /></button>
          ) : <Truck className="h-5 w-5" />}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{data.ruta}</p>
            <p className="text-xs text-white/80">{d ? `${d.codigo} · ${d.fecha}${d.placa ? ` · ${d.placa}` : ''}` : 'Sin reparto activo hoy'}</p>
          </div>
        </div>
      </header>

      {!d ? (
        <div className="p-8 text-center text-muted-foreground">
          <Clock className="mx-auto mb-2 h-8 w-8" />
          No hay un despacho en ruta para hoy. Cuando la oficina ponga el despacho en ruta, aparecerá aquí.
        </div>
      ) : parada ? (
        <FormularioEntrega despachoId={d.id} parada={parada} token={token}
          onListo={() => { setAbierta(null); void qc.invalidateQueries({ queryKey: ['reparto', token] }); }} />
      ) : (
        <div className="space-y-2 p-3">
          <CompartirUbicacion token={token} />
          <TempTransito token={token} />
          <p className="px-1 text-xs text-muted-foreground">{d.paradas.filter((p) => p.entrega).length}/{d.paradas.length} paradas registradas</p>
          {d.paradas.map((p) => (
            <button key={p.contrato_id} onClick={() => setAbierta(p.contrato_id)}
              className="flex w-full items-center gap-3 rounded-lg border bg-white p-3 text-left shadow-sm active:bg-accent dark:bg-slate-800">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">{p.orden}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{p.cliente}</p>
                <p className="truncate text-xs text-muted-foreground"><MapPin className="mr-1 inline h-3 w-3" />{p.direccion}</p>
                <p className="text-xs text-muted-foreground">{p.raciones} raciones{p.hora_objetivo ? ` · ${p.hora_objetivo.slice(0, 5)}` : ''}</p>
              </div>
              {p.entrega ? (
                p.entrega.estado === 'entregado'
                  ? <CheckCircle2 className="h-6 w-6 shrink-0 text-green-600" />
                  : <AlertTriangle className="h-6 w-6 shrink-0 text-amber-500" />
              ) : <span className="shrink-0 rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">pendiente</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CompartirUbicacion({ token }: { token: string }) {
  const [on, setOn] = useState(false);
  const watchRef = useRef<number | null>(null);
  const lastSent = useRef(0);
  useEffect(() => {
    if (!on) return;
    if (!('geolocation' in navigator)) { toast.error('Este dispositivo no tiene GPS'); setOn(false); return; }
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => {
        const now = Date.now();
        if (now - lastSent.current < 25000) return; // throttle 25 s
        lastSent.current = now;
        void apiClient.post('/rpc/reparto_posicion', {
          _token: token, _lat: p.coords.latitude, _lon: p.coords.longitude,
          _velocidad: p.coords.speed != null ? Math.round(p.coords.speed * 3.6 * 10) / 10 : null,
        }).catch(() => undefined);
      },
      () => { toast.error('No se pudo obtener la ubicación'); setOn(false); },
      { enableHighAccuracy: true, maximumAge: 20000, timeout: 15000 },
    );
    return () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); };
  }, [on, token]);
  return (
    <button onClick={() => setOn((v) => !v)}
      className={`flex w-full items-center gap-2 rounded-lg border p-2.5 text-sm font-medium shadow-sm ${on ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'bg-white dark:bg-slate-800'}`}>
      <MapPin className={`h-5 w-5 ${on ? 'text-emerald-600' : 'text-muted-foreground'}`} />
      {on ? 'Compartiendo ubicación con la oficina…' : 'Compartir mi ubicación'}
      {on && <span className="ml-auto h-2.5 w-2.5 animate-pulse rounded-full bg-emerald-500" />}
    </button>
  );
}

function TempTransito({ token }: { token: string }) {
  const [temp, setTemp] = useState('');
  const [tipo, setTipo] = useState('caliente');
  const reg = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/reparto_temp', { _token: token, _tipo: tipo, _temp_c: Number(temp) })).data,
    onSuccess: () => { setTemp(''); toast.success('Temperatura registrada'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <div className="flex items-center gap-2 rounded-lg border bg-white p-2 shadow-sm dark:bg-slate-800">
      <Thermometer className="h-5 w-5 shrink-0 text-sky-600" />
      <span className="text-sm font-medium">Temp. en tránsito</span>
      <select className="rounded border bg-transparent px-1 py-1 text-sm" value={tipo} onChange={(e) => setTipo(e.target.value)}>
        <option value="caliente">caliente</option><option value="frio">frío</option>
      </select>
      <input type="number" inputMode="decimal" step="0.1" className="w-16 rounded border bg-transparent px-2 py-1 text-right text-sm" placeholder="°C" value={temp} onChange={(e) => setTemp(e.target.value)} />
      <button disabled={reg.isPending || temp === ''} onClick={() => reg.mutate()}
        className="ml-auto rounded-md bg-sky-600 px-3 py-1.5 text-sm font-semibold text-white active:bg-sky-700 disabled:opacity-50">Registrar</button>
    </div>
  );
}

function FormularioEntrega({ despachoId, parada, token, onListo }: {
  despachoId: string; parada: Parada; token: string; onListo: () => void;
}) {
  const [temp, setTemp] = useState('');
  const [receptor, setReceptor] = useState(parada.entrega?.receptor ?? '');
  const [cedula, setCedula] = useState('');
  const [firma, setFirma] = useState('');
  const [foto, setFoto] = useState('');
  const [obs, setObs] = useState('');
  const [incidencia, setIncidencia] = useState('');
  const [modo, setModo] = useState<'entrega' | 'incidencia'>('entrega');

  const enviar = useMutation({
    mutationFn: (estado: 'entregado' | 'incidencia') => apiClient.post('/rpc/reparto_entregar', {
      _token: token,
      _payload: {
        despacho_id: despachoId, contrato_id: parada.contrato_id, estado,
        temp_llegada_c: temp === '' ? null : temp, receptor_nombre: receptor || null, receptor_id: cedula || null,
        firma: firma || null, foto: foto || null, observaciones: obs || null,
        incidencia_motivo: estado === 'incidencia' ? (incidencia || 'Otro') : null,
      },
    }),
    onSuccess: (_r, estado) => { toast.success(estado === 'entregado' ? 'Entrega registrada' : 'Incidencia registrada'); onListo(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4 p-4">
      <div className="rounded-lg border bg-white p-3 dark:bg-slate-800">
        <p className="font-semibold">{parada.cliente}</p>
        <p className="text-xs text-muted-foreground"><MapPin className="mr-1 inline h-3 w-3" />{parada.direccion}</p>
        <p className="pt-1 text-sm">{parada.raciones} raciones · {parada.servicios}</p>
        {parada.telefono && <a href={`tel:${parada.telefono}`} className="mt-1 inline-flex items-center gap-1 text-sm text-emerald-700"><Phone className="h-3.5 w-3.5" /> {parada.telefono}</a>}
        {parada.entrega && (
          <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            Ya registrada como <strong>{parada.entrega.estado}</strong>{parada.entrega.receptor ? ` (${parada.entrega.receptor})` : ''}. Puedes corregirla.
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <button onClick={() => setModo('entrega')} className={`flex-1 rounded-lg border py-2 text-sm font-medium ${modo === 'entrega' ? 'border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30' : ''}`}>Entrega conforme</button>
        <button onClick={() => setModo('incidencia')} className={`flex-1 rounded-lg border py-2 text-sm font-medium ${modo === 'incidencia' ? 'border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-900/30' : ''}`}>Incidencia</button>
      </div>

      <div className="space-y-3 rounded-lg border bg-white p-3 dark:bg-slate-800">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Temperatura de llegada (°C)</label>
          <input className="h-11 w-full rounded-md border px-3 text-base" type="number" inputMode="decimal" step="0.5" placeholder="ej. 64" value={temp} onChange={(e) => setTemp(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Nombre de quien recibe</label>
          <input className="h-11 w-full rounded-md border px-3 text-base" placeholder="Nombre y apellido" value={receptor} onChange={(e) => setReceptor(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Cédula (opcional)</label>
          <input className="h-11 w-full rounded-md border px-3 text-base" inputMode="numeric" placeholder="Cédula del receptor" value={cedula} onChange={(e) => setCedula(e.target.value)} />
        </div>

        {modo === 'incidencia' && (
          <div>
            <label className="text-xs font-medium text-muted-foreground">Motivo de la incidencia</label>
            <select className="h-11 w-full rounded-md border px-3 text-base" value={incidencia} onChange={(e) => setIncidencia(e.target.value)}>
              <option value="">Selecciona…</option>
              {INCIDENCIAS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}

        {modo === 'entrega' && <FirmaCanvas value={firma} onChange={setFirma} label="Firma del receptor" />}
        <FotoCaptura value={foto} onChange={setFoto} label="Foto (entrega / novedad)" />

        <div>
          <label className="text-xs font-medium text-muted-foreground">Observaciones</label>
          <textarea className="w-full rounded-md border px-3 py-2 text-base" rows={2} value={obs} onChange={(e) => setObs(e.target.value)} />
        </div>
      </div>

      <button disabled={enviar.isPending}
        onClick={() => enviar.mutate(modo === 'entrega' ? 'entregado' : 'incidencia')}
        className={`w-full rounded-lg py-3 text-base font-semibold text-white shadow active:opacity-90 ${modo === 'entrega' ? 'bg-emerald-600' : 'bg-amber-600'} disabled:opacity-50`}>
        {enviar.isPending ? 'Guardando…' : modo === 'entrega' ? 'Confirmar entrega' : 'Registrar incidencia'}
      </button>
    </div>
  );
}
